import { createStore } from 'zustand/vanilla';
import type { RenderResult, TocEntry } from '@markroom/renderer';
import type { FileSource, OpenedDocument, RecentEntry } from '../filesource/types';
import { DocumentLoadError, makeDocument } from '../filesource/document';

export type ThemePref = 'light' | 'dark' | 'system';
export type DocStatus = 'empty' | 'loading' | 'ready' | 'error';

export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.1;

export interface ViewerState {
  doc: OpenedDocument | null;
  render: RenderResult | null;
  toc: TocEntry[];
  status: DocStatus;
  error: string | null;
  recents: RecentEntry[];

  themePref: ThemePref;
  zoom: number;
  sidebarOpen: boolean;
  readingWidth: boolean;

  findOpen: boolean;
  findQuery: string;
  findCount: number;
  findCurrent: number; // 1-based index of the active match, 0 when none

  editMode: 'view' | 'edit' | 'split';
  editBuffer: string | null; // null when not editing
  isDirty: boolean;
  /** A save this session captured a writable target for the current document,
   *  so subsequent saves can overwrite silently (Model B). Reset on open/new. */
  savedOnce: boolean;
  isSaving: boolean;
  saveError: string | null;
  /** Bumped every time `render`/`toc` are replaced (open, live preview, new).
   *  Render-keyed hooks (find, reading progress) react to this so they stay in
   *  sync with the live preview, whose doc id does not change while editing. */
  renderVersion: number;

  // actions
  openDocument: (doc: OpenedDocument) => Promise<void>;
  closeDocument: () => void;
  setLoadError: (message: string) => void;
  reloadRecents: () => Promise<void>;
  openRecent: (entry: RecentEntry) => Promise<void>;
  openRelativeLink: (href: string) => Promise<void>;

  setThemePref: (pref: ThemePref) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleReadingWidth: () => void;

  setFindOpen: (open: boolean) => void;
  setFindQuery: (q: string) => void;
  setFindStatus: (count: number, current: number) => void;

  enterEdit: () => void;
  setEditMode: (mode: 'view' | 'edit' | 'split') => void;
  setEditBuffer: (text: string) => void;
  discardEdits: () => void;
  resetEditState: () => void;
  previewBuffer: (text: string) => Promise<void>;
  newDocument: () => Promise<void>;
  save: () => Promise<void>;
  exportAs: () => Promise<void>;
  clearSaveError: () => void;
  /** Internal: adopt a freshly-written document (re-keyed id, recents refresh). */
  applySaved: (saved: OpenedDocument) => Promise<void>;
}

export type ViewerStore = ReturnType<typeof createViewerStore>;

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 10) / 10));

function errorMessage(err: unknown): string {
  if (err instanceof DocumentLoadError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong while opening the document.';
}

/**
 * Leave-guard for unsaved edits. Returns true if it's OK to proceed (discard).
 * A blocking confirm is the minimal honest guard for Phase A; Phase C replaces
 * it with a styled dialog. Falls back to allowing the action where `confirm`
 * is unavailable (e.g. non-DOM test environments) — callers only invoke it when
 * already dirty.
 */
function confirmDiscardUnsaved(): boolean {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true;
  return window.confirm('Discard unsaved changes?');
}

export function createViewerStore(source: FileSource, initialThemePref: ThemePref = 'system') {
  return createStore<ViewerState>((set, get) => ({
    doc: null,
    render: null,
    toc: [],
    status: 'empty',
    error: null,
    recents: [],

    themePref: initialThemePref,
    zoom: 1,
    sidebarOpen: true,
    readingWidth: false,

    findOpen: false,
    findQuery: '',
    findCount: 0,
    findCurrent: 0,

    editMode: 'view',
    editBuffer: null,
    isDirty: false,
    savedOnce: false,
    isSaving: false,
    saveError: null,
    renderVersion: 0,

    async openDocument(doc) {
      // Leave-guard: don't silently discard an unsaved buffer when an explicit
      // open (Open, drag/drop, recents, OS open) would replace the document.
      if (get().isDirty && !confirmDiscardUnsaved()) return;
      set({
        status: 'loading',
        error: null,
        doc,
        findOpen: false,
        findQuery: '',
        editMode: 'view',
        editBuffer: null,
        isDirty: false,
        savedOnce: false,
        saveError: null,
      });
      try {
        const { renderMarkdown } = await import('@markroom/renderer');
        const result = await renderMarkdown(doc.text, {
          // The adapter returns a loadable URL, or null when inaccessible (the
          // renderer then omits src so alt text shows). Remote URLs are passed
          // through by the adapter itself — we must NOT fall back to the raw src
          // for relative paths, or the browser would request them against origin.
          transformImageUrl: (src) => source.resolveAssetUrl(doc, src),
        });
        // Ignore if another document started loading meanwhile.
        if (get().doc?.id !== doc.id) return;
        set({
          render: result,
          toc: result.toc,
          status: 'ready',
          error: null,
          renderVersion: get().renderVersion + 1,
        });
        source.notifyDocumentOpened(doc);
        await source.rememberRecent(doc);
        await get().reloadRecents();
      } catch (err) {
        if (get().doc?.id !== doc.id) return;
        set({ status: 'error', error: errorMessage(err), render: null, toc: [] });
      }
    },

    closeDocument() {
      set({
        doc: null,
        render: null,
        toc: [],
        status: 'empty',
        error: null,
        findOpen: false,
        findQuery: '',
        findCount: 0,
        findCurrent: 0,
      });
      source.notifyDocumentOpened(null);
    },

    setLoadError(message) {
      // A failed open (e.g. an oversized/unsupported drop) must not silently
      // discard an unsaved buffer. Confirm first; declining keeps the user
      // editing and ignores the error.
      if (get().isDirty && !confirmDiscardUnsaved()) return;
      set({
        status: 'error',
        error: message,
        doc: null,
        render: null,
        toc: [],
        editMode: 'view',
        editBuffer: null,
        isDirty: false,
        savedOnce: false,
        saveError: null,
      });
    },

    async reloadRecents() {
      set({ recents: await source.listRecent() });
    },

    async openRecent(entry) {
      // openDocument owns the loading/leave-guard; setting status here would
      // strand it on 'loading' if the user cancels the discard prompt.
      try {
        const doc = await source.openRecent(entry);
        if (!doc) {
          await source.forgetRecent(entry.id);
          await get().reloadRecents();
          set({ status: get().doc ? 'ready' : 'empty', error: null });
          return;
        }
        await get().openDocument(doc);
      } catch (err) {
        // Route through setLoadError so a recent-open failure honors the dirty
        // leave-guard instead of stranding an unsaved buffer behind an error.
        get().setLoadError(errorMessage(err));
      }
    },

    // A relative link (`./other.md`) is resolved and opened as a sibling
    // document where the platform can reach it; where it can't (or the target
    // isn't accessible/markdown), this is a no-op — the current document must
    // never be destroyed by a link click (see App.tsx's click handler, which
    // always preventDefault()s non-hash links before calling this).
    async openRelativeLink(href) {
      const doc = get().doc;
      if (!doc) return;
      const target = await source.openRelativeLink(doc, href);
      if (target) await get().openDocument(target);
    },

    setThemePref(pref) {
      set({ themePref: pref });
    },
    zoomIn() {
      set({ zoom: clampZoom(get().zoom + ZOOM_STEP) });
    },
    zoomOut() {
      set({ zoom: clampZoom(get().zoom - ZOOM_STEP) });
    },
    zoomReset() {
      set({ zoom: 1 });
    },
    toggleSidebar() {
      set({ sidebarOpen: !get().sidebarOpen });
    },
    setSidebarOpen(open) {
      set({ sidebarOpen: open });
    },
    toggleReadingWidth() {
      set({ readingWidth: !get().readingWidth });
    },

    setFindOpen(open) {
      set({ findOpen: open, ...(open ? {} : { findQuery: '', findCount: 0, findCurrent: 0 }) });
    },
    setFindQuery(q) {
      set({ findQuery: q });
    },
    setFindStatus(count, current) {
      set({ findCount: count, findCurrent: current });
    },

    enterEdit() {
      const doc = get().doc;
      if (!doc) return;
      set({ editMode: 'edit', editBuffer: doc.text, isDirty: false });
    },
    setEditMode(mode) {
      const doc = get().doc;
      // Seed the buffer the first time we enter an editing mode.
      const editBuffer = get().editBuffer ?? (mode !== 'view' ? (doc?.text ?? '') : null);
      set({ editMode: mode, editBuffer });
    },
    setEditBuffer(text) {
      const doc = get().doc;
      set({ editBuffer: text, isDirty: text !== (doc?.text ?? '') });
    },
    discardEdits() {
      const doc = get().doc;
      set({ editBuffer: doc?.text ?? null, isDirty: false });
    },
    resetEditState() {
      set({ editMode: 'view', editBuffer: null, isDirty: false });
    },
    async previewBuffer(text) {
      const { renderMarkdown } = await import('@markroom/renderer');
      const doc = get().doc;
      const result = await renderMarkdown(text, {
        transformImageUrl: (src) => (doc ? source.resolveAssetUrl(doc, src) : null),
      });
      // Ignore a stale render: if the buffer moved on (or a new document opened,
      // which nulls editBuffer) while we were rendering, don't clobber the newer
      // preview with this older result.
      if (get().editBuffer !== text) return;
      set({ render: result, toc: result.toc, renderVersion: get().renderVersion + 1 });
    },
    async newDocument() {
      if (get().isDirty && !confirmDiscardUnsaved()) return;
      const doc = await makeDocument('Untitled.md', new TextEncoder().encode(''), null);
      const { renderMarkdown } = await import('@markroom/renderer');
      const result = await renderMarkdown('', {});
      set({
        doc,
        render: result,
        toc: result.toc,
        status: 'ready',
        error: null,
        editMode: 'edit',
        editBuffer: '',
        isDirty: false,
        savedOnce: false,
        saveError: null,
        findOpen: false,
        findQuery: '',
        renderVersion: get().renderVersion + 1,
      });
      source.notifyDocumentOpened(doc);
    },

    // After a successful write: adopt the re-keyed document (new content-hash id,
    // possibly new name/locator from Save-As), clear dirty, and refresh recents.
    // Keeps the editor buffer in sync only if we were editing.
    async applySaved(saved) {
      const editing = get().editBuffer !== null;
      set({
        doc: saved,
        editBuffer: editing ? saved.text : null,
        isDirty: false,
        savedOnce: true,
        isSaving: false,
        saveError: null,
      });
      source.notifyDocumentOpened(saved);
      await source.rememberRecent(saved);
      await get().reloadRecents();
    },

    async save() {
      const { doc, editBuffer, isSaving, savedOnce } = get();
      if (!doc || editBuffer === null || isSaving) return;
      set({ isSaving: true, saveError: null });
      try {
        // Model B: silent overwrite once we hold a writable target; otherwise the
        // first save (and New docs, and platforms without a silent target) routes
        // through the Save dialog defaulting to the origin.
        const saved =
          savedOnce && source.canSaveSilently(doc)
            ? await source.saveExisting(doc, editBuffer)
            : await source.saveAs(doc, doc.name, editBuffer);
        if (!saved) {
          set({ isSaving: false }); // user cancelled the dialog — stay dirty, no error
          return;
        }
        await get().applySaved(saved);
      } catch (err) {
        set({ isSaving: false, saveError: errorMessage(err) });
      }
    },

    async exportAs() {
      const { doc, editBuffer, isSaving } = get();
      if (!doc || isSaving) return;
      // Export the live buffer when editing, else the rendered document's text.
      const text = editBuffer ?? doc.text;
      set({ isSaving: true, saveError: null });
      try {
        const saved = await source.saveAs(doc, doc.name, text);
        if (!saved) {
          set({ isSaving: false });
          return;
        }
        await get().applySaved(saved);
      } catch (err) {
        set({ isSaving: false, saveError: errorMessage(err) });
      }
    },

    clearSaveError() {
      set({ saveError: null });
    },
  }));
}
